import {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import InputText from "./InputText";
import { ImageAPI } from "../services/APIConnection";
import { mainTable } from "../css/Common";

const FileUploadDynamic = forwardRef(
  (
    {
      individualFileSize,
      totalFileSize,
      questionNumber,
      partnerId,
      defaultAttachments = [],
      values,
      attachmentFilesData,
      setParentButtonDisabled,
      attachmentFilesValidData,
      onRemove,
      createdBy,
    },
    ref
  ) => {
    const [items, setItems] = useState([]); // items = [{ type: 'file' | 'url', ... }]
    const [errors, setErrors] = useState([]);
    const [showAttachmentModal, setShowAttachmentModal] = useState(false);
    const [selectedURL, setSelectedURL] = useState("");
    const [activeIndex, setActiveIndex] = useState(null);
    const [showFileModal, setShowFileModal] = useState(false);
    const [selectedFileUrl, setSelectedFileUrl] = useState("");
    const [disabledStates, setDisabledStates] = useState({});
    const [fileSelectedStates, setFileSelectedStates] = useState({});
    const fileInputRefs = useRef({});
    const [showConfirmIndex, setShowConfirmIndex] = useState(null);
    const [flag, setFlag] = useState("");
    const extension = selectedFileUrl?.split(".").pop().toLowerCase();

    const getViewerUrl = (url) => {
      if (!url) return "";
      const extension = url.split(".").pop().toLowerCase();

      return extension === "pdf"
        ? url
        : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
            url
          )}`;
    };
    useEffect(() => {
      if (defaultAttachments.length > 0) {
        const initialFiles = defaultAttachments
          .filter((att) => att.pages && att.pages.trim() !== "") // ✅ Filter out attachments where pages is empty/null
          .map((att) => ({
            type: att.type,
            file: {
              name: decodeURIComponent(att.url?.split("/").pop() || ""),
              isDefault: true,
            },
            url: att.url,
            pages: att.pages,
            comment: att.comment,
          }));

        setItems(initialFiles); // ✅ Now only attachments with non-empty pages
        setErrors(Array(initialFiles.length).fill({}));
        values(initialFiles);
        setParentButtonDisabled(true);
      }
    }, []);

    useImperativeHandle(ref, () => ({
      handleUploadSubmit: () => handleUpload(),
      validateAndSubmit: () => validateFiles(),
    }));
    const isURLInAttachmentFiles = (url) => {
      return attachmentFilesData.some((att) => att.AttachedDocument === url);
    };
    const handleSelectExisting = () => {
      
      if (!selectedURL) return;
      const selectedAttachment = attachmentFilesData.find(
        (att) => att.AttachedDocument === selectedURL
      );

      if (activeIndex !== null) {
        // ✅ Replace existing item (URL input focus case)
        const newItems = [...items];
        newItems[activeIndex].url = selectedURL;
        newItems[activeIndex].pages = selectedAttachment?.Pages || "";
        newItems[activeIndex].comment = selectedAttachment?.Comment || "";
        setItems(newItems);
      } else {
        // ✅ Add new item (Refer existing document button case)
        setItems([
          ...items,
          {
            type: "url",
            url: selectedURL,
            pages: selectedAttachment?.Pages || "",
            comment: selectedAttachment?.Comment || "",
          },
        ]);
        setErrors([...errors, {}]);
      }

      setShowAttachmentModal(false);
      setActiveIndex(null); // Reset activeIndex
      setParentButtonDisabled(false);
    };

    const validateFiles = () => {
      const newErrors = items.map((item) => {
        const error = {};
        if (item.type == "file" && !item.url) {
          error.file = "File is required.";
        }
        if (item.type == "url" && !isURLInAttachmentFiles(item.url.trim())) {
          error.url = "URL is required.";
        }
        if (!item.pages?.trim()) {
          error.pages = "Indicated Pages is required.";
        }
        return error;
      });
      setErrors(newErrors);
      return newErrors.every((e) => Object.keys(e).length === 0);
    };

    const handleInputChange = (index, field, value) => {
      const newItems = [...items];
      newItems[index][field] = value;
      setItems(newItems);
      values(newItems);
      setDisabledStates((prev) => ({
        ...prev,
        [index]: false,
      }));
      setParentButtonDisabled(false);
    };

    const handleFileChange = (event, index) => {
      const file = event.target.files[0];
      const newErrors = [...errors];

      setDisabledStates((prev) => ({
        ...prev,
        [index]: false,
      }));
      setParentButtonDisabled(false);

      if (!file) {
        newErrors[index] = { file: "File is required." };
        setErrors(newErrors);
        setFileSelectedStates((prev) => ({ ...prev, [index]: false }));
        return;
      } else {
        setFileSelectedStates((prev) => ({ ...prev, [index]: true }));
      }

      const validTypes = [
        "application/pdf",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "image/jpeg",
        "image/png",
      ];

      const allowedExtensions = [
        "pdf",
        "xls",
        "xlsx",
        "doc",
        "docx",
        "ppt",
        "pptx",
        "jpeg",
        "png",
      ];

      const maxSingleFileSize = individualFileSize * 1024 * 1024;
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (file.size > maxSingleFileSize) {
        newErrors[index] = {
          file: `File size is more than ${individualFileSize} MB.`,
        };
        setErrors(newErrors);
        return;
      }

      if (
        !validTypes.includes(file.type) &&
        !allowedExtensions.includes(fileExtension)
      ) {
        newErrors[index] = { file: "Upload correct format." };
        setErrors(newErrors);
        return;
      }

      // ✅ Just update the state with file object; don't upload yet
      const newItems = [...items];
      newItems[index].file = file;
      newItems[index].url = null; // Reset the URL
      setItems(newItems);
      newErrors[index] = {};
      setErrors(newErrors);
    };
    const handleUpload = async () => {
      // 
      const newErrors = [...errors];
      const updatedItems = [...items];
      let uploadFailed = false; // ✅ Track if any upload fails

      for (let index = 0; index < updatedItems.length; index++) {
        const item = updatedItems[index];
        const file = item.file;

        if (file && !item.url) {
          
          const formData = new FormData();
          formData.append("Attachment", file);
          formData.append("PartnerId", partnerId);
          formData.append("QuestionNumber", questionNumber);
          formData.append("TotalAttachmentsSize", totalFileSize);
          formData.append("FileName", file.name);
          formData.append("DocumentName", file.name);
          formData.append("DocumentRepositoryId", totalFileSize);
          formData.append("CreatedBy", createdBy);
          try {
            const response = await ImageAPI(
              formData,
              "AnswerQuestionsAPI/AnswersFileUpload"
            );
            updatedItems[index].url = response.data;
            newErrors[index] = {};
          } catch (error) {
            uploadFailed = true; // ✅ Mark that at least one file upload failed

            if (error.response?.status === 409) {
              newErrors[index] = {
                file: 'The file already exists. You may refer to that file using the "Refer Existing Document" Button.',
              };
            } else if (error.response?.status === 400) {
              newErrors[index] = { file: `Exceeding ${totalFileSize} MB.` };
            } else {
              newErrors[index] = {
                file: "Error uploading file. Please try again.",
              };
            }
          }
        }
      }

      setItems(updatedItems);
      values(updatedItems);
      setErrors(newErrors);

      return !uploadFailed; // ✅ Return true if all uploads succeeded, false if any failed
    };

    const confirmDelete = (index) => {
      // 
      const itemToDelete = items[index];
      const fileUrl = itemToDelete?.url;
      const fileType = itemToDelete?.type;

      if (!fileUrl || fileType !== "file") {
        handleRemove(index);
        setShowConfirmIndex(null);
        return;
      }

      const fileName = decodeURIComponent(fileUrl.split("/").pop() || "");
      const isReferred = attachmentFilesValidData.some((file) => {
        const existingFileName = decodeURIComponent(
          file?.AttachedDocument?.split("/").pop() || ""
        );
        setParentButtonDisabled(false);
        return existingFileName === fileName;
      });

      // if (isReferred) {
      //   setErrors((prev) => {
      //     const updated = [...prev];
      //     updated[index] = {
      //       file: `This document is currently referred to in other questions. Kindly remove all references before proceeding with deletion.`,
      //     };
      //     return updated;
      //   });
      // } else {
      //   handleRemove(index);
      // }

      // if(fileType === "file"){
      handleRemove(index);
      // }
      setShowConfirmIndex(null);
    };

    const handleAddFile = () => {
      setItems([
        ...items,
        { type: "file", file: null, url: "", pages: "", comment: "" },
      ]);
      setErrors([{}]);
      setParentButtonDisabled(false);
    };

    const handleRemove = async (index) => {
      // 
      const item = items[index];
      const fileUrl = item?.url;
      const fileType = item?.type;

      setFileSelectedStates((prev) => ({ ...prev, [index]: false }));

      try {
        
        // if (fileUrl && fileType === "file") {
        //   await JSONAPI(
        //     { FileUrl: fileUrl },
        //     "AnswerQuestionsAPI/AnswersFileDelete"
        //   );
        // }

        const updatedItems = items.filter((_, i) => i !== index);
        const updatedErrors = errors.filter((_, i) => i !== index);

        setItems(updatedItems);
        setErrors(updatedErrors);
        setDisabledStates((prev) => {
          const { [index]: _, ...rest } = prev;
          return rest;
        });

        if (fileInputRefs.current[index])
          fileInputRefs.current[index].value = "";

        setParentButtonDisabled(updatedItems.length === 0);
        values(updatedItems);

        if (typeof onRemove === "function" && item?.url) {
          onRemove(updatedItems, true);
        }
      } catch (error) {
        console.error("Delete failed:", error);
        const errorMessage =
          error?.response?.data?.title ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete file.";

        setErrors((prev) => {
          const updated = [...prev];
          updated[index] = { file: errorMessage };
          return updated;
        });
      } finally {
        setDisabledStates((prev) => ({ ...prev, [index]: false }));
      }
    };

    return (
      <div className="space-y-4">
        {/* Show added items below */}
        {items.map((item, index) => (
          <div
            key={index}
            className="bg-gray-100 p-4 rounded-lg border space-y-3 relative  "
          >
            <div className="absolute top-2 right-2 group">
              <button
                onClick={() => {
                  if (item?.url) {
                    setFlag(1);
                    setShowConfirmIndex(index);
                  } else {
                    handleRemove(index);
                  }
                }}
                className="text-blue-700 hover:text-blue-900"
              >
                ✖
              </button>

              {/* Tooltip */}
              <div className="absolute -top-8 right-0 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 z-20 whitespace-nowrap">
                Remove Uploaded or Referred Document
              </div>
            </div>

            {/* Confirm Dialog */}
            {showConfirmIndex === index && flag === 1 && (
              <div className="absolute top-12 right-0 z-10 bg-white border border-gray-300 rounded-lg shadow-md p-3 w-64">
                <p className="text-gray-800 mb-2 text-sm">
                  Are you sure to remove the reference?
                </p>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setShowConfirmIndex(null)}
                    className="px-3 py-1 text-gray-600 rounded hover:bg-gray-100 text-sm"
                  >
                    No
                  </button>
                  <button
                    onClick={() => confirmDelete(index)}
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                  >
                    Yes
                  </button>
                </div>
              </div>
            )}

            <h4 className="font-semibold">
              {item.type === "file"
                ? item.url !="" ? "Referred document" : "Attach document"
                : "Referred document"}
              <span className="text-red-500">*</span>
            </h4>

            {item.type === "file" ? (
              <>
                {!item.file?.name && !item.url && (
                  <div className="relative w-full">
                    <input
                      ref={(el) => (fileInputRefs.current[index] = el)}
                      type="file"
                      onChange={(e) => handleFileChange(e, index)}
                      disabled={item.file?.name && item.url}
                      className="border bg-white border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-10"
                    />
                  </div>
                )}
                {item.file?.name && (
                  <p
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedFileUrl(item.url); // set state to show in modal
                      setShowFileModal(true); // open modal
                    }}
                    className="text-blue-600 underline break-words cursor-pointer"
                  >
                    {item.file.name}
                  </p>
                )}

                {errors[index]?.file && (
                  <p className="text-red-500 text-sm">{errors[index].file}</p>
                )}
              </>
            ) : (
              <>
                {!item.file?.name && !item.url && (
                  <input
                    type="text"
                    placeholder="Enter URL"
                    value={decodeURIComponent(item.url?.split("/").pop() || "")}
                    onFocus={() => {
                      setActiveIndex(index); // track which item is being edited
                      setSelectedURL("");
                      setShowAttachmentModal(true);
                    }}
                    onChange={(e) => {
                      setParentButtonDisabled(false);
                      handleInputChange(index, "url", e.target.value);
                    }}
                    className={mainTable.input}
                  />
                )}
                {item.url && (
                  <p
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedFileUrl(item.url); // set state to show in modal
                      setShowFileModal(true); // open modal
                    }}
                    className="text-blue-600 underline break-words cursor-pointer"
                  >
                    {decodeURIComponent(item.url?.split("/").pop() || "") || ""}
                  </p>
                )}
                {errors[index]?.url && (
                  <p className="text-red-500 text-sm">{errors[index].url}</p>
                )}
              </>
            )}

            <InputText
              inputType="text"
              title="Indicated Pages"
              name="indicatedPages"
              content={item.pages}
              controlFunc={(e) =>
                handleInputChange(index, "pages", e.target.value)
              }
              maxLength={100}
              classStar="text-red-500"
            />
            {errors[index]?.pages && (
              <p className="text-red-500 text-sm">{errors[index].pages}</p>
            )}

            <textarea
              placeholder="Comment (optional)"
              className="w-full border rounded px-2 py-1 text-sm"
              value={item.comment}
              maxLength={500}
              onChange={(e) =>
                handleInputChange(index, "comment", e.target.value)
              }
            />
          </div>
        ))}

        <div className="text-xs text-red-500 italic whitespace-normal block">
          Attachment file size limit: {individualFileSize} MB. Accepted file
          types are PDF, XLS, XLSX, DOC, DOCX, PPT, PPTX, JPG, JPEG, PNG. Total
          file size limit for the entire Q&A should not exceed {totalFileSize}{" "}
          MB.
        </div>
        {showAttachmentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 max-w-lg w-full space-y-3 shadow-lg">
              <h3 className="text-lg font-semibold">
                Select Existing Document
              </h3>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {attachmentFilesData.map((att, idx) => (
                  <label key={idx} className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="existingAttachment"
                      value={att.AttachedDocument}
                      checked={selectedURL === att.AttachedDocument}
                      onChange={(e) => setSelectedURL(e.target.value)}
                    />
                    <span className="break-words text-sm text-gray-800">
                      {decodeURIComponent(
                        att.AttachedDocument?.split("/").pop() || ""
                      )}
                    </span>
                  </label>
                ))}
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  onClick={() => setShowAttachmentModal(false)}
                  className="px-3 py-1 rounded bg-gray-300 hover:bg-gray-400 text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSelectExisting}
                  disabled={!selectedURL}
                  className="px-3 py-1 rounded bg-gray-600 hover:bg-gray-700 text-white disabled:bg-gray-300"
                >
                  Select
                </button>
              </div>
            </div>
          </div>
        )}
        {showFileModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowFileModal(false)} // Outside click closes modal
          >
            <div
              className="bg-white p-6 rounded shadow-lg max-w-4xl w-full h-full relative"
              onClick={(e) => e.stopPropagation()} // Prevent close on inside click
            >
              <button
                onClick={() => setShowFileModal(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-black text-xl"
              >
                ✕
              </button>
              {selectedFileUrl ? (
                extension === "pdf" ||
                ["doc", "docx", "ppt", "pptx", "xls", "xlsx"].includes(
                  extension
                ) ? (
                  <iframe
                    src={getViewerUrl(selectedFileUrl)}
                    title="File Viewer"
                    className="w-full h-[80vh] rounded"
                  />
                ) : extension === "txt" ? (
                  <iframe
                    src={selectedFileUrl}
                    title="Text File Viewer"
                    className="w-full h-[80vh] rounded bg-white text-left p-4"
                  />
                ) : ["jpg", "jpeg", "png"].includes(extension) ? (
                  <img
                    src={selectedFileUrl}
                    alt="Preview"
                    className="w-full h-auto max-h-[80vh] object-contain rounded"
                  />
                ) : (
                  <p className="text-center text-gray-600">
                    Preview not available for this file type.
                  </p>
                )
              ) : (
                <p className="text-center text-gray-600">
                  No file selected or file not available.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Always show the two buttons first */}
        <div className="flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0 mb-4">
          <button
            onClick={handleAddFile}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 w-full sm:w-auto"
          >
            Add new document
          </button>
          {attachmentFilesData != 0 && (
            <button
              onClick={() => {
                setActiveIndex(null);
                setShowAttachmentModal(true);
                setSelectedURL("");
                // setParentButtonDisabled(false);
              }}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 w-full sm:w-auto"
            >
              Refer existing document
            </button>
           )} 
        </div>
      </div>
    );
  }
);

export default FileUploadDynamic;
